import React, {useCallback, useEffect, useState} from "react";
import * as Sentry from '@sentry/browser';
import {FormattedMessage} from "react-intl";
import Api from "../../Api";
import TimelineEvent from "./TimelineEvent";
import classes from "./styles.module.css";

const EVENT_TYPES = [
    {value: 'MERCHANT_REQUEST', label: 'Запрос от мерчанта'},
    {value: 'MERCHANT_RESPONSE', label: 'Ответ мерчанту'},
    {value: 'MERCHANT_CALLBACK', label: 'Callback мерчанту'},
    {value: 'MERCHANT_CALLBACK_MISSING', label: 'Callback не настроен'},
    {value: 'PROVIDER_REQUEST', label: 'Запрос к провайдеру'},
    {value: 'PROVIDER_RESPONSE', label: 'Ответ от провайдера'},
    {value: 'PROVIDER_CALLBACK', label: 'Callback от провайдера'},
    {value: 'STATUS_CHANGE', label: 'Изменение статуса'},
    {value: 'FRAUD_CHECK', label: 'Проверка антифрода'},
    {value: 'FRAUD_BLOCK', label: 'Блокировка'},
    {value: 'SYSTEM_NOTE', label: 'Системная заметка'},
];

const DIRECTIONS = [
    {value: '', label: 'Все'},
    {value: 'IN', label: 'Входящие (IN)'},
    {value: 'OUT', label: 'Исходящие (OUT)'},
];

function TransactionAudit({transactionId, token, initialData}) {
    const [loading, setLoading] = useState(!initialData);
    const [error, setError] = useState(null);
    const [data, setData] = useState(initialData || null);
    const [exporting, setExporting] = useState(false);

    // Filters state
    const [selectedEventTypes, setSelectedEventTypes] = useState([]);
    const [selectedDirection, setSelectedDirection] = useState('');
    const [filtersOpen, setFiltersOpen] = useState(false);

    const fetchData = useCallback(async () => {
        if (!transactionId || !token) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const filters = {};
            if (selectedEventTypes.length > 0) {
                filters.eventTypes = selectedEventTypes;
            }
            if (selectedDirection) {
                filters.direction = selectedDirection;
            }

            const result = await new Api({token}).getTransactionAudit(transactionId, filters);
            setData(result);
        } catch (err) {
            Sentry.captureException(err);
            setError(err.message || "Не удалось загрузить данные аудита");
        } finally {
            setLoading(false);
        }
    }, [transactionId, token, selectedEventTypes, selectedDirection]);

    // Перезапрашиваем только при изменении фильтров (не при первом монтировании, если есть initialData)
    useEffect(() => {
        var hasFilters = selectedEventTypes.length > 0 || selectedDirection;
        if (hasFilters || !initialData) {
            fetchData();
        }
    }, [fetchData, initialData, selectedEventTypes, selectedDirection]);

    const handleEventTypeToggle = (eventType) => {
        setSelectedEventTypes(prev =>
            prev.includes(eventType)
                ? prev.filter(t => t !== eventType)
                : [...prev, eventType]
        );
    };

    const handleClearFilters = () => {
        setSelectedEventTypes([]);
        setSelectedDirection('');
    };

    const handleExport = async (format) => {
        if (!transactionId || !token) return;

        setExporting(true);
        try {
            const blob = await new Api({token}).exportTransactionAudit(transactionId, format);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `audit_${transactionId}.${format}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Export failed:', err);
            alert('Export failed: ' + (err.message || 'Unknown error'));
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <div className={classes.Loading}>
                <FormattedMessage id="TransactionAudit.loading" defaultMessage="Загрузка данных..."/>
            </div>
        );
    }

    if (error) {
        return (
            <div className={classes.Error}>
                <FormattedMessage id="TransactionAudit.error" defaultMessage="Ошибка: {error}" values={{error}}/>
            </div>
        );
    }

    if (!data) {
        return (
            <div className={classes.Empty}>
                <FormattedMessage id="TransactionAudit.empty" defaultMessage="Нет данных аудита"/>
            </div>
        );
    }

    const {events = []} = data;

    // events уже содержат все данные в нужном формате от API
    const allEvents = events
        .map(e => ({...e, source: 'timeline'}))
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    const hasActiveFilters = selectedEventTypes.length > 0 || selectedDirection;

    return (
        <div className={classes.Container}>
            <div className={classes.Header}>
                <FormattedMessage id="TransactionAudit.title" defaultMessage="Аудит транзакций"/>
                <span className={classes.TransactionId}>#{transactionId}</span>

                <div className={classes.HeaderActions}>
                    <button
                        className={`${classes.FilterToggle} ${hasActiveFilters ? classes.FilterActive : ''}`}
                        onClick={() => setFiltersOpen(!filtersOpen)}
                    >
                        <FormattedMessage id="TransactionAudit.filters" defaultMessage="Фильтры"/>
                        {hasActiveFilters && <span
                            className={classes.FilterBadge}>{selectedEventTypes.length + (selectedDirection ? 1 : 0)}</span>}
                    </button>

                    <button
                        className={classes.ExportBtn}
                        onClick={() => handleExport('json')}
                        disabled={exporting}
                        title="Export JSON"
                    >
                        JSON
                    </button>
                    <button
                        className={classes.ExportBtn}
                        onClick={() => handleExport('csv')}
                        disabled={exporting}
                        title="Export CSV"
                    >
                        CSV
                    </button>
                </div>
            </div>

            {filtersOpen && (
                <div className={classes.FiltersPanel}>
                    <div className={classes.FilterGroup}>
                        <div className={classes.FilterLabel}>
                            <FormattedMessage id="TransactionAudit.eventTypes" defaultMessage="Типы событий"/>
                        </div>
                        <div className={classes.FilterChips}>
                            {EVENT_TYPES.map(({value, label}) => (
                                <button
                                    key={value}
                                    className={`${classes.FilterChip} ${selectedEventTypes.includes(value) ? classes.FilterChipActive : ''}`}
                                    onClick={() => handleEventTypeToggle(value)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={classes.FilterGroup}>
                        <div className={classes.FilterLabel}>
                            <FormattedMessage id="TransactionAudit.direction" defaultMessage="Направление"/>
                        </div>
                        <div className={classes.FilterChips}>
                            {DIRECTIONS.map(({value, label}) => (
                                <button
                                    key={value}
                                    className={`${classes.FilterChip} ${selectedDirection === value ? classes.FilterChipActive : ''}`}
                                    onClick={() => setSelectedDirection(value)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {hasActiveFilters && (
                        <button className={classes.ClearFilters} onClick={handleClearFilters}>
                            <FormattedMessage id="TransactionAudit.clearFilters" defaultMessage="Сбросить фильтры"/>
                        </button>
                    )}
                </div>
            )}

            {allEvents.length === 0 ? (
                <div className={classes.Empty}>
                    <FormattedMessage id="TransactionAudit.noEvents" defaultMessage="События пока не записаны"/>
                </div>
            ) : (
                <div className={classes.Timeline}>
                    {allEvents.map((event, index) => (
                        <TimelineEvent key={event.id || index} event={event} isLast={index === allEvents.length - 1}/>
                    ))}
                </div>
            )}

        </div>
    );
}

export default TransactionAudit;



// WEBPACK FOOTER //
// ./src/Components/TransactionAudit/TransactionAudit.js